import { PaginationArrowRight } from 'assets/icons/PaginationArrowRight'
import { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { usePagination } from './usePagination'

const getPages = (pageCount: number, currentPage: number) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, '...', pageCount]
  }

  if (currentPage >= pageCount - 3) {
    return [1, '...', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount]
  }

  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', pageCount]
}

export const Pagination = ({ itemCount, limit }: { itemCount: number; limit: number }) => {
  const { currentPage, setCurrentPage } = usePagination()
  const pageCount = Math.ceil(itemCount / limit)
  const arrayToDisplay = useMemo(() => getPages(pageCount, currentPage), [pageCount, currentPage])

  if (pageCount < 2) return <></>

  return (
    <Container>
      <ArrowButton
        direction='left'
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        <PaginationArrowRight />
      </ArrowButton>
      <PagesContainer>
        {arrayToDisplay.map((page: number | string, index: number) => (
          <Button
            key={index + page.toString()}
            onClick={() => (typeof page === 'number' ? setCurrentPage(page) : null)}
            active={currentPage === page}
          >
            {page}
          </Button>
        ))}
      </PagesContainer>
      <ArrowButton
        direction='right'
        disabled={currentPage === pageCount}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        <PaginationArrowRight />
      </ArrowButton>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PagesContainer = styled.div`
  border: 1px solid ${(props) => props.theme.tableRowBorder};
  background: ${(props) => props.theme.dividerColor};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.textColor};
`

const Button = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => props.theme.textColor};
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  font-weight: 700;
  background: ${(props) => (props.active ? props.theme.colorOrange : 'none')};
`

const ArrowButton = styled.button<{ direction: 'left' | 'right' }>`
  border: 1px solid ${(props) => props.theme.textColor};
  color: ${(props) => props.theme.textColor};
  cursor: pointer;
  background: none;
  border-radius: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.colorOrange};
    color: ${(props) => props.theme.colorOrange};
  }

  &:disabled {
    border-color: ${(props) => props.theme.textFaded};
    color: ${(props) => props.theme.textFaded};
    cursor: not-allowed;
  }

  ${(props) =>
    props.direction === 'left' &&
    css`
      transform: rotate(180deg);
    `}
`
